"use client"

import type { NearConnector } from "@hot-labs/near-connect"
import type { AbstractWalletConnect } from "@hot-labs/near-connect/build/types"
import type {
  SignMessageParams,
  SignedMessage,
} from "@near-wallet-selector/core/src/lib/wallet/wallet.types"
import { ChainType } from "@src/hooks/useConnectWallet"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import { useLastConnectedWalletStore } from "@src/stores/useLastConnectedWalletStore"
import type { SignAndSendTransactionsParams } from "@src/types/interfaces"
import { logger } from "@src/utils/logger"
import { getDomainMetadataParams } from "@src/utils/whitelabelDomainMetadata"
import type { providers } from "near-api-js"
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

interface NearWalletContextValue {
  connector: NearConnector | null
  accountId: string | null
  connectedWalletId: string | null
  isInitializing: boolean
  ensureInitialized: () => Promise<NearConnector | null>
  connect: (walletId?: string) => Promise<void>
  disconnect: () => Promise<void>
  signMessage: (message: SignMessageParams) => Promise<{
    signatureData: SignedMessage
    signedData: SignMessageParams
  }>
  signAndSendTransactions: (
    params: SignAndSendTransactionsParams
  ) => Promise<providers.FinalExecutionOutcome[]>
}

const NearWalletContext = createContext<NearWalletContextValue | null>(null)

const NEAR_WALLET_ID_KEY = "near_connected_wallet_id"

export const NearWalletProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [connector, setConnector] = useState<NearConnector | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  const [connectedWalletId, setConnectedWalletId] = useState<string | null>(
    () =>
      typeof window !== "undefined"
        ? localStorage.getItem(NEAR_WALLET_ID_KEY)
        : null
  )
  const [isInitializing, setIsInitializing] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("chainType") === ChainType.Near
  )
  const { whitelabelTemplate } = useContext(FeatureFlagsContext)

  // Stable refs so init() doesn't depend on reactive state
  const connectorRef = useRef<NearConnector | null>(null)
  const initPromiseRef = useRef<Promise<NearConnector | null | undefined>>(null)
  const pendingWalletIdRef = useRef<string | null>(null)

  const init = useCallback(async () => {
    // Dedupe: return the in-flight promise if init is already running
    if (initPromiseRef.current) {
      return initPromiseRef.current
    }
    // Already initialized
    if (connectorRef.current) {
      return connectorRef.current
    }

    const doInit = async () => {
      const { NearConnector } = await import(
        "@hot-labs/near-connect/build/NearConnector"
      )

      const params = getDomainMetadataParams(whitelabelTemplate)
      let walletConnect: AbstractWalletConnect | undefined
      if (params.core.projectId != null && params.core.projectId !== "") {
        try {
          const { SignClient } = await import("@walletconnect/sign-client")
          walletConnect = await SignClient.init({
            projectId: params.core.projectId,
            metadata: params.metadata,
          })
        } catch (err) {
          logger.error(err)
        }
      }

      let newConnector: NearConnector | null = null

      try {
        newConnector = new NearConnector({
          network: "mainnet",
          walletConnect,
        })
      } catch (err) {
        logger.error(err)
        return
      }

      newConnector.on("wallet:signOut", () => {
        setAccountId(null)
        setConnectedWalletId(null)
        localStorage.removeItem(NEAR_WALLET_ID_KEY)
      })
      newConnector.on("wallet:signIn", (t) => {
        setAccountId(t.accounts?.[0]?.accountId ?? null)
        const walletId =
          pendingWalletIdRef.current ?? localStorage.getItem(NEAR_WALLET_ID_KEY)
        if (walletId) {
          setConnectedWalletId(walletId)
          localStorage.setItem(NEAR_WALLET_ID_KEY, walletId)
          useLastConnectedWalletStore
            .getState()
            .recordLastConnectedWallet({ chain: "near", walletId })
        }
      })

      // Resolve accounts before publishing state — this is the key invariant.
      // connector + accountId + isInitializing must update coherently so
      // downstream consumers never see settled + null address mid-restore.
      try {
        const wallet = await newConnector.wallet()
        const accounts = await wallet.getAccounts()
        if (accounts.length > 0) {
          setAccountId(accounts[0].accountId)
        } else {
          localStorage.removeItem("chainType")
        }
      } catch (err) {
        logger.error(err)
        localStorage.removeItem("chainType")
      }

      connectorRef.current = newConnector
      setConnector(newConnector)
      return newConnector
    }

    initPromiseRef.current = doInit()
    try {
      return await initPromiseRef.current
    } finally {
      initPromiseRef.current = null
    }
  }, [whitelabelTemplate])

  useEffect(() => {
    const prevChainType = localStorage.getItem("chainType")
    if (prevChainType === ChainType.Near) {
      init().finally(() => setIsInitializing(false))
    }
  }, [init])

  const connect = useCallback(
    async (walletId?: string) => {
      const newConnector = connectorRef.current ?? (await init())
      if (newConnector) {
        pendingWalletIdRef.current = walletId ?? null
        try {
          await newConnector.connect(walletId ? { walletId } : undefined)
          localStorage.setItem("chainType", ChainType.Near)
          if (walletId) {
            setConnectedWalletId(walletId)
            localStorage.setItem(NEAR_WALLET_ID_KEY, walletId)
            useLastConnectedWalletStore
              .getState()
              .recordLastConnectedWallet({ chain: "near", walletId })
          }
        } finally {
          pendingWalletIdRef.current = null
        }
      }
    },
    [init]
  )

  // Trigger connector + manifest init without prompting a wallet connection.
  // Used by the chain-container modal so the NEAR wallet list is populated
  // when the user opens the picker, not only after they pick a wallet.
  const ensureInitialized = useCallback(async () => {
    const c = connectorRef.current ?? (await init()) ?? null
    if (c) {
      await c.whenManifestLoaded
    }
    return c
  }, [init])

  const disconnect = useCallback(async () => {
    const cur = connectorRef.current
    if (!cur) return
    // Remove chainType before disconnect — connector.disconnect() triggers
    // re-renders that may re-mount this provider and re-trigger init()
    localStorage.removeItem("chainType")
    localStorage.removeItem(NEAR_WALLET_ID_KEY)
    try {
      await cur.disconnect()
    } catch (err) {
      logger.error(err)
    }
    // Always clear accountId — connector.disconnect() may throw before
    // emitting wallet:signOut (e.g. "No accounts found" when the session
    // is stale), leaving accountId set and blocking sign-out.
    // NOTE: Do NOT setConnector(null) here — keeping the connector alive
    // allows instant reconnect without re-initializing WalletConnect.
    setAccountId(null)
    setConnectedWalletId(null)
  }, [])

  const signMessage = useCallback(async (message: SignMessageParams) => {
    const cur = connectorRef.current
    if (!cur) {
      throw new Error("Connector not initialized")
    }
    const wallet = await cur.wallet()
    const accounts = await wallet.getAccounts()
    if (accounts.length === 0) {
      throw new Error(
        "No accounts available for signing. Please reconnect your wallet."
      )
    }
    const signatureData = await wallet.signMessage(message)
    return { signatureData, signedData: message }
  }, [])

  const signAndSendTransactions = useCallback(
    async (params: SignAndSendTransactionsParams) => {
      const cur = connectorRef.current
      if (!cur) {
        throw new Error("Connector not initialized")
      }
      const wallet = await cur.wallet()
      return wallet.signAndSendTransactions(params)
    },
    []
  )

  const value = useMemo<NearWalletContextValue>(() => {
    return {
      connector,
      accountId,
      connectedWalletId,
      isInitializing,
      ensureInitialized,
      connect,
      disconnect,
      signMessage,
      signAndSendTransactions,
    }
  }, [
    connector,
    accountId,
    connectedWalletId,
    isInitializing,
    ensureInitialized,
    connect,
    disconnect,
    signMessage,
    signAndSendTransactions,
  ])

  return (
    <NearWalletContext.Provider value={value}>
      {children}
    </NearWalletContext.Provider>
  )
}

export function useNearWallet() {
  const ctx = useContext(NearWalletContext)
  if (!ctx) {
    throw new Error("useNearWallet must be used within a NearWalletProvider")
  }
  return ctx
}
